import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { IncludedRes, InvoiceItemRes, InvoiceRes, UnbilledItemRes } from '../types/invoice.type'

type Props = Partial<{
  page: number
  limit: number
  sortDir: 'asc' | 'desc'
  id: string
  status: string
  encounter_id: string
  created_from: string
  unbilledId: string
  query: string
  /** Opt in to the unfiltered GET /invoices list (billing/pagination/includedData). Most callers
   * only want a single invoice (`id`) or an encounter's items (`encounter_id`) and never read the
   * list, so it's off by default rather than firing on every useBilling call. */
  fetchList: boolean
}>

export const useBilling = ({
  limit,
  page,
  id,
  status,
  sortDir = 'desc',
  encounter_id,
  created_from,
  unbilledId,
  query,
  fetchList
}: Props) => {
  const { data: billing, isLoading } = useQuery({
    enabled: !!fetchList,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<InvoiceRes[], IncludedRes[]>>('invoices', {
        params: {
          ...(sortDir && { sort_dir: sortDir }),
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(status && { status: status === 'all' ? '' : status }),
          ...(encounter_id && { encounter_id }),
          ...(query && { query })
        }
      })
      return data
    },
    queryKey: ['invoices', { encounter_id, id, limit, page, sortDir, status, query }]
  })
  const { data: invoiceItem, isLoading: invoiceItemIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      let endpoint = `/invoices/${id}/invoice_items?`
      if (created_from) endpoint += `created_from=${created_from}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<InvoiceItemRes[]>>(endpoint)
      return data
    },
    queryKey: ['invoice', { created_from, id }]
  })

  // Fetches the single Invoice resource (status, discount, prior_outstanding_balance, etc.) by its own id.
  // Callers that need "the invoice for encounter X" must resolve `id` from the encounter's own
  // `invoice` relationship (see useEncounter) rather than guessing from an unfiltered invoices list.
  // Keyed under the same `['invoice', ...]` prefix as invoiceItem/encounterInvoiceItem so every
  // mutation below (which invalidates `['invoice']`) refetches it too — a plain `['invoice_resource', ...]`
  // key would never be invalidated by those calls, and with staleTime: Infinity would never refetch
  // on its own, leaving the Invoice tab's status/discount/amount paid stale after a discount or payment.
  const { data: invoiceRes, isLoading: invoiceIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<InvoiceRes>>(`/invoices/${id}`)
      return data
    },
    queryKey: ['invoice', 'resource', { id }]
  })

  const { data: encounterInvoiceItem, isLoading: encounterInvoiceItemIsLoading } = useQuery({
    enabled: !!encounter_id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<InvoiceItemRes[]>>(
        `encounters/${encounter_id}/invoice_items`
      )
      return data
    },
    queryKey: ['invoice', { encounter_id }]
  })

  const { mutateAsync: createInvoice } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post(`invoices/${id}/invoice_items`, formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    }
  })

  const { mutateAsync: updateItem } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`invoice_items/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    }
  })

  const { mutateAsync: updateInvoice } = useMutation({
    mutationFn: async ({ formVal, id: invoiceId }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`invoices/${invoiceId}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    }
  })

  const { mutateAsync: deleteItem } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`invoice_items/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    }
  })

  const { data: unbilledItems, isLoading: unbilledItemsIsLoading } = useQuery({
    enabled: !!unbilledId,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnbilledItemRes>>(
        `invoices/${unbilledId}/unbilled_items`
      )
      return data
    },
    queryKey: ['invoices', { unbilledId }]
  })

  return {
    billing: billing?.data,
    billingIsLoading: isLoading,
    createInvoice,
    deleteItem,
    encounterInvoiceItem: encounterInvoiceItem?.data,
    encounterInvoiceItemIsLoading,
    includedData: billing?.included,
    invoice: invoiceRes?.data,
    invoiceIsLoading,
    invoiceItem: invoiceItem?.data,
    invoiceItemIsLoading,
    pagination: billing?.meta?.pagination,
    updateItem,
    updateInvoice,
    unbilledItems: unbilledItems?.data,
    unbilledItemsIsLoading
  }
}
